// lib/utils.ts
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import axios, { AxiosError } from 'axios';
import { extractStructuredProviderErrorMessage } from '#lib/provider-errors';
import { DEFAULT_KEYBOARD_SHORTCUTS } from '#lib/keyboard-shortcuts';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const defaultShortcuts: { [key: string]: string } = DEFAULT_KEYBOARD_SHORTCUTS;

export const imageDataToFile = async (
  imageData: ImageData | null,
  fileName: string = 'masked_content.png', // Default to PNG for transparency
  mimeType: string = 'image/png'
): Promise<File | null> => {
  if (!imageData) return null;
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(new File([blob], fileName, { type: mimeType }));
      } else {
        resolve(null);
      }
    }, mimeType); // Use PNG by default to preserve transparency
  });
};

export const copyTextToClipboard = async (value: string): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  if (typeof document === 'undefined') {
    throw new Error('Clipboard not available');
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

export const numberOrDefault = (value: unknown, fallback: number): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : fallback
);

export const getFileExtension = (filename: string): string | undefined => {
  return filename.split('.').pop()?.toLowerCase();
};

export const getSpecificErrorMessage = (error: any, fallback?: string): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>; // Type assertion for better access
    if (axiosError.response && axiosError.response.data) {
      const providerMessage = extractStructuredProviderErrorMessage(axiosError.response.data);
      if (providerMessage) return providerMessage;

      // Prefer the 'message' field in response.data if it's a non-empty string
      if (typeof axiosError.response.data.message === 'string' && axiosError.response.data.message.trim() !== "") {
        return axiosError.response.data.message;
      }
      // Fallback to 'error' field in response.data if it's a non-empty string (and message wasn't suitable)
      if (typeof axiosError.response.data.error === 'string' && axiosError.response.data.error.trim() !== "") {
        return axiosError.response.data.error;
      }
    }
    // If no specific message in response.data, or no response.data, use Axios's generic message
    return axiosError.message; // e.g., "Request failed with status code 500"
  }
  if (error instanceof Error) {
    return error.message; // For standard JavaScript errors
  }
  // Caller-provided label first, then fallback for non-Error types or null/undefined
  return fallback ?? String(error || 'An unknown processing error occurred');
};
